import { PopoverFailedComponent } from '@1bill-app/components/popover-failed/popover-failed.component';
import { PopoverPayingRateInfoComponent } from '@1bill-app/components/popover/popover-paying-rate-info/popover-paying-rate-info.component';
import { PopoverSimpleComponent } from '@1bill-app/components/popover/popover-simple/popover-simple.component';
import {
  APP_CONFIG,
  BILL_FEE,
  COLOR_RATING_VALUES,
  EXPLANATION_NOTES_SIMPLE,
  ROUTE_CONTACT_US,
  ROUTE_HOMEPAGE,
  ROUTE_SETTINGS_CONTACT_DETAILS,
  ROUTE_SETTINGS_PAYMENT_INFO,
} from '@1bill-app/constants';
import { generateArray } from '@1bill-app/helpers/array.helper';
import { formatDateChart } from '@1bill-app/helpers/date.helpers';
import { getChartTitle } from '@1bill-app/helpers/general.helper';
import {
  ApiService,
  SwitchProcessCallbackResponse,
} from '@1bill-app/services/api/api.service';
import { BillQuery } from '@1bill-app/services/bill/bill.query';
import { BillService } from '@1bill-app/services/bill/bill.service';
import { BillStore } from '@1bill-app/services/bill/bill.store';
import { billIsAwaitingApproval, isBillOverdue, isBillPaid } from '@1bill-app/services/bill/helper';
import {
  AppProductType,
  appProductTypeDefaultImg,
  appProductTypes,
  BillCategoryKey,
  GetBillOption,
  PaidPaymentMethod,
  SwitchErrorCode,
  ViewBillType,
} from '@1bill-app/services/bill/types';
import { CategoryService } from '@1bill-app/services/category/category.service';
import { HomeService } from '@1bill-app/services/home/home.service';
import { PaymentCard } from '@1bill-app/services/payment/payment-card.service';
import { SettingsQuery } from '@1bill-app/services/settings/settings.query';
import { SettingsService } from '@1bill-app/services/settings/settings.service';
import { YodleeService } from '@1bill-app/services/yodlee/yodlee.service';
import { YodleePossibleTransaction } from '@1bill-app/services/yodlee/yodlee.types';
import {
  Component,
  ElementRef,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  ActionSheetController,
  AlertController,
  IonContent,
  LoadingController,
  ModalController,
  NavController,
  PopoverController,
  ToastController,
} from '@ionic/angular';
import { ActionSheetOptions } from '@ionic/core';
import { sum, orderBy, first } from 'lodash';
import { NGXLogger } from 'ngx-logger';
import { of } from 'rxjs';
import { map, mergeMap, tap } from 'rxjs/operators';
import { SubSink } from 'subsink';
import { canSwitch } from '@1bill-app/services/bill/helper';
import { BetterDealService } from '@1bill-app/services/better-deal/better-deal.service';
import { BetterDealQuery } from '@1bill-app/services/better-deal/better-deal.query';
import { UserQuery } from '@1bill-app/services/user/user.query';
import { UIService } from '@1bill-app/services/ui.service';

/**
 * Page for showing details of individual bills.
 */
@Component({
  selector: 'app-bill',
  templateUrl: './bill.page.html',
  styleUrls: ['./bill.page.scss'],
})
export class BillPage implements OnInit, OnDestroy {
  constructor(
    private billQuery: BillQuery,
    private billService: BillService,
    private billStore: BillStore,
    private categorySvc: CategoryService,
    private homeService: HomeService,
    private yodleeService: YodleeService,
    private api: ApiService,
    private settingsService: SettingsService,
    private logger: NGXLogger,
    private ngZone: NgZone,
    private router: Router,
    private route: ActivatedRoute,
    private navCtrl: NavController,
    private toastController: ToastController,
    private loadingController: LoadingController,
    private actionSheetController: ActionSheetController,
    private popoverController: PopoverController,
    private alertController: AlertController,
    private settingsQuery: SettingsQuery,
    private betterDealService: BetterDealService,
    private betterDealQuery: BetterDealQuery,
    private userQuery: UserQuery,
    private uiService: UIService,
  ) {}

  @ViewChild('infoDetails') infoDetails: ElementRef;

  @ViewChild(IonContent, {
    static: false,
  })
  content: IonContent;

  @ViewChild('subtitleTextEdit', {
    static: false,
  })
  subtitleTextEdit: any;
  @Input('isPending') isAwaitingVerification = false;
  @Input() isTechnicalIssue = false;
  paidPaymentMethod = PaidPaymentMethod;
  _source: string;
  showMoreInfo = false;
  billDetails$ = this.billQuery.billDetails$;
  billLoading$ = this.billQuery.isLoading$;
  billDetails: ViewBillType;
  billPaymentId: number;
  jobId: number;
  accountBillTypeId;
  billPaid = false;
  titleLogo: string;
  categories: AppProductType[];
  public BillCategoryKey = BillCategoryKey;
  infoDetailsHeight = 0;
  tagName = null;
  billRating: number;
  isBillEditing = false;
  isBillFailed = false;
  subs = new SubSink();
  private readonly TAG_MIN_LENGTH = 3;
  public isBillLoading$ = this.billQuery.isLoading$;
  switchRewardPoints$ = this.billQuery.switchRewardPoints$;
  getChartTitle = getChartTitle;
  billIsAwaitingApproval = billIsAwaitingApproval;
  duplicateName: string;
  billTagExists = false;
  tagTooShort = false;
  savingTag = false;
  gotInitialTag = false;
  accountCards: PaymentCard[];
  accountCards$ = this.settingsQuery.accountCards$;
  betterDealProcessing$ = this.betterDealQuery.select('processing');

  /**
   * Bill's PDF Url.
   * If null, we assume the bill was added manually on the `add-bill-manually` page.
   */
  pdfUrl: string;

  /**
   * All bills matching the type of the current one. Used for validating non-duplicate tags.
   */
  matchingBillsOfType: ViewBillType[];

  tagRegex = {
    expression: '^[-_&() a-zA-Z0-9.$!#@]+$',
    valid: true,
  };

  get userCanAddBills() {
    return this.userQuery.userCanAddBills;
  }

  get userHasFullAccess() {
    return this.userQuery.userHasFullAccess;
  }

  get averageEnergyUsage() {
    const isUsage = this.billDetails?.barChartData?.every((x) => x.usage);
    if (!isUsage) return 'No Data';
    const average =
      sum(this.billDetails?.barChartData?.map((x) => x?.usage)) /
        this.billDetails?.barChartData?.length ?? 1;
    return average;
  }

  get energyUnit() {
    switch (this.billDetails?.categoryKey) {
      case BillCategoryKey.ELECTRICITY:
        if (this.billDetails?.provider === 'AGL Energy') return '$';
        return 'kWh';
      case BillCategoryKey.GAS:
        return 'MJ';
      default:
        return 'kWh';
    }
  }

  get earliestDate() {
    const isDate = this.billDetails?.barChartData?.every((x) => x.date);
    if (!isDate) return null;

    const orderedValue = orderBy(
      this.billDetails?.barChartData?.map((v) => ({
        ...v,
        order: v?.bbox[0],
      })),
      ['order'],
    );
    const date = first(orderedValue)?.date;
    return formatDateChart(date, true);
  }

  get fee() {
    return this.billDetails.amount * BILL_FEE;
  }

  get totalPayAmount() {
    return this.billDetails.amount + this.fee;
  }

  get hasSupplierLogo() {
    return !!this.billDetails?.supplierLogoUrl?.length;
  }

  ngOnInit() {
    this._source = decodeURIComponent(
      this.route.snapshot.queryParamMap.get('_source') || '/app/bills',
    );
    this.logger.debug('_source:', this._source);
    this.billService.getBill().subscribe();
    // Get bill payment id or job id
    let billPaymentId = this.route.snapshot.paramMap.get('id');

    this.route.params.subscribe((param) => {
      const { id } = param;
      billPaymentId = id;
    });

    const jobId = this.route.snapshot.queryParams?.jobId;

    if (!billPaymentId) {
      billPaymentId = this.route.snapshot.queryParams?.billPaymentId;
      this.logger.log('billPaymentId', billPaymentId);
      this.billPaymentId = Number(billPaymentId);

      this.billStore.update({
        currentBillId: this.billPaymentId,
      });
      this.logger.debug('Using bill payment id: ' + this.billPaymentId);
    } else {
      this.jobId = Number(jobId);

      this.billStore.update({
        currentJobId: this.jobId,
      });
      this.logger.debug('Using job id: ' + this.jobId);
      this.billDetails$ = this.billQuery.failedBillDetails$;
      this.isBillFailed = true;
    }

    this.subs.sink = this.billDetails$.subscribe((res) => {
      if (res.length) {
        this.billDetails = res[0];
        this.accountBillTypeId = this.billDetails.accountBillTypeId;

        if (!this.gotInitialTag) {
          this.tagName = this.billDetails.billTag;

          this.gotInitialTag = true;
        }

        // this.billDetails.datePaid ? (this.billPaid = true) : (this.billPaid = false);
        this.billPaid = isBillPaid(this.billDetails);
        this.titleLogo = this.billDetails.supplierLogoUrl;
        this.billRating = this.billDetails.rating;

        // Set matching bills of type once current bill's details are fetched
        this.billQuery
          .getBillsForView(GetBillOption.ALL_BILLS)
          .pipe(
            map((bills) =>
              bills.filter(
                (bill) =>
                  bill.categoryKey === this.billDetails.categoryKey &&
                  bill.accountBillTypeId !== this.billDetails.accountBillTypeId,
              ),
            ),
          )
          .subscribe((viewBills) => {
            this.matchingBillsOfType = viewBills;
          });
      }
    });

    this.route.queryParams.subscribe(async (p) => {
      this.logger.log('received params:', p);

      if (p.refetchBills) {
        await this.fetchData();
      }

      // Only perform update switch code if the cncRefId exists in the query params
      if (p.cncRefId) {
        await this.billService.updateSwitch(p as SwitchProcessCallbackResponse).finally(async () => {
          await this.fetchData();
          this.router.navigate(['.'], {
            relativeTo: this.route,
            queryParams: {
              billPaymentId: this.billPaymentId,
            }
          });
        });
      }
    });

    this.subs.sink = this.homeService.homeDataEvent$.subscribe((trigger) => {
      if (trigger) {
        this.logger.debug('BillPage::homeDataEvent$', trigger);
        this.subscribeAllData();
      }
    });
    this.subs.sink = this.yodleeService.getAccounts().subscribe();
    this.subs.sink = this.settingsService.fetch().subscribe();

    //Account Cards
    this.settingsQuery.accountCards$.subscribe({
      next: (accountCards) => {
        this.accountCards = accountCards;
      },

      error: (err) => {
        this.logger.error('Unable to retrieve user account cards from data store:', err);
      },
    });

    this.getPdfUrl();
  }

  // Get PDF Url
  async getPdfUrl() {
    this.api.getPaymentFileBlobUri(this.billPaymentId, 'bill').subscribe({
      next: (res) => {
        if (res.buffer == false) this.pdfUrl = encodeURIComponent(res.url);
        else this.pdfUrl = res;
        this.logger.log('PDF URL:', this.pdfUrl);
      },
      error: (err) => {
        this.pdfUrl = 'N/A';
        this.logger.log('PDF URL:', this.pdfUrl);
      },
    });
  }

  validateTagName() {
    this.tagTooShort = this.tagName?.length > 0 && this.tagName?.length < this.TAG_MIN_LENGTH;
    this.checkDuplicateBillTags();
    this.validateRegex(this.tagRegex);
  }

  /**
   * Takes a regular expression, validates it, and returns true if valid.
   * @param regex Regex to be tested in the form of { expression: <Regular expression>, valid: boolean }
   * @returns True if valid; false if invalid
   */
  private validateRegex(regex: { expression: string | RegExp; valid: boolean }) {
    const match = this.tagName?.match(regex.expression);
    regex.valid = this.tagName?.length === 0 || !!match;
    return !regex.valid;
  }

  private checkDuplicateBillTags() {
    this.billTagExists = this.matchingBillsOfType?.some(
      (bill) => bill.billTag === this.tagName,
    );
    return this.billTagExists;
  }

  getPaymentCardNumber(bill: ViewBillType) {
    // this.logger.log(bill);
    if (bill.accountOrCardNo)
      return '(using ****' + bill.accountOrCardNo.slice(bill.accountOrCardNo.length - 4) + ')';
    else return '';
  }

  editTag() {
    if (!this.userCanAddBills) {
      this.alertController
        .create({
          header: 'Not authorised',
          message:
            'You cannot edit this tag as the owner of this account has enabled you view-only access.',
          buttons: ['OK'],
        })
        .then((alert) => {
          alert.present();
        });
      return;
    }
    this.isBillEditing = !this.isBillEditing;
    this.subtitleTextEdit?.setFocus();
  }

  getCardIconUrl(bill: ViewBillType) {
    if (bill.supplierLogoUrl) {
      return bill.supplierLogoUrl;
    } else {
      return (
        appProductTypes.find((p) => p.key === bill.categoryKey)?.imgUrl ||
        appProductTypeDefaultImg
      );
    }
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
  }

  subscribeAllData() {
    this.subs.sink = this.homeService.fetchAllData().subscribe();
  }

  async fetchData() {
    const loading = await this.loadingController.create({
      message: 'Loading data...',
    });
    await loading.present();

    this.billService.fetchBill(true).subscribe(() => {
      loading.dismiss();
    });
  }

  async presentInfoPopoverManualPayment() {
    const popover = await this.popoverController.create({
      component: PopoverSimpleComponent,
      cssClass: 'onebill-popover-simple',
      // translucent: true,
      mode: 'ios',
      componentProps: {
        popoverTitle: EXPLANATION_NOTES_SIMPLE.MANUAL_PAYMENT_TITLE,

        /* If user has payment cards in their account, go to `payment-info`
         * Else, go to `payment-options`
         * Display relevant description based on this.*/
        description:
          this.accountCards?.length > 0
            ? EXPLANATION_NOTES_SIMPLE.MANUAL_PAYMENT_DESC_CARDS
            : EXPLANATION_NOTES_SIMPLE.MANUAL_PAYMENT_DESC_NO_CARDS,
        link:
          this.accountCards?.length > 0
            ? EXPLANATION_NOTES_SIMPLE.MANUAL_PAYMENT_LINK_CARDS
            : EXPLANATION_NOTES_SIMPLE.MANUAL_PAYMENT_LINK_NO_CARDS,
      },
    });
    return await popover.present();
  }

  async presentInfoPopoverEditTag() {
    const popover = await this.popoverController.create({
      component: PopoverSimpleComponent,
      cssClass: 'onebill-popover-simple',
      // translucent: true,
      mode: 'ios',
      componentProps: {
        popoverTitle: EXPLANATION_NOTES_SIMPLE.EDIT_TAG_TITLE,
        description: EXPLANATION_NOTES_SIMPLE.EDIT_TAG_DESC,
      },
    });
    return await popover.present();
  }

  async presentInfoPopoverRating() {
    const popover = await this.popoverController.create({
      component: PopoverPayingRateInfoComponent,
      cssClass: 'ratings-popover',
      // translucent: true,
      // mode: 'ios',
    });
    return await popover.present();
  }

  async presentMoreActionSheet() {
    const actionSheetConfig: ActionSheetOptions = {
      cssClass: 'bill-action-sheet',
      buttons: [
        {
          text: 'Delete bill',
          cssClass: 'delete-button',
          handler: () => this.confirmDeleteBill(),
        },

        {
          text: 'Cancel',
          role: 'cancel',
          cssClass: 'cancel-button',
        },
      ],
      mode: 'ios',
    };

    if (this.billDetails && this.billDetails.isDirectDebitSetup == 1) {
      actionSheetConfig.buttons = [
        ...[
          {
            text: 'Direct Debit not enabled',
            cssClass: 'normal-button',
            handler: () => this.confirmDirectDebitUpdate(),
          },
        ],
        ...actionSheetConfig.buttons,
      ];
    }

    const actionSheet = await this.actionSheetController.create(actionSheetConfig);
    await actionSheet.present();
  }

  async presentPopover() {
    const popover = await this.popoverController.create({
      component: PopoverFailedComponent,
      cssClass: 'my-custom-class',
      // event: ev,
      translucent: true,
    });
    return await popover.present();
  }

  async presentCannotSwitchModal(switchErrorCode: SwitchErrorCode, message: string) {
    let text1 = 'You are unable to switch at this time';
    let text2 = message;
    let buttonText = '';
    let route = '';
    const routeSource = this.router.url;

    if (switchErrorCode === SwitchErrorCode.MobileUnverified) {
      buttonText = 'Verify mobile number';
      route = ROUTE_SETTINGS_CONTACT_DETAILS;
    }

    if (switchErrorCode === SwitchErrorCode.MultipleMobile) {
      buttonText = 'Please contact our support team.';
      route = ROUTE_CONTACT_US;
    }

    await this.uiService.presentActionDeniedModal(
      text1,
      text2,
      buttonText,
      route,
      routeSource,
    );
  }

  toggleMoreInfo() {}

  async presentTncPopUp() {
    this.betterDealService.presentTncPopUp(this.billDetails);
  }

  async errorAlert(customMessage?: string) {
    const alert = await this.alertController.create({
      mode: 'ios',
      cssClass: 'bill-alert alert-icon',
      message: customMessage
        ? `<img src="assets/icon/alert/warning.svg"alt="warning-icon"/><h2 class="alert-title">Oops !</h2><p class="alert-text">$ {
          customMessage
        }

        </p>`
        : `<img src="assets/icon/alert/warning.svg"alt="warning-icon"/><h2 class="alert-title">Oops !</h2><p class="alert-text">Something went wrong ! Please try again later.</p>`,
      buttons: [
        {
          text: 'Cancel',
          role: 'cancel',
        },

        {
          text: 'Got it',
          handler: () => {},
        },
      ],
    });

    await alert.present();
  }
  getDetails(categoryKey: BillCategoryKey) {
    return this.categorySvc.getCategoryDetails(categoryKey);
  }

  getRatingColor(rating: any) {
    if (isNaN(Number(rating))) return null;
    const RatingColors = COLOR_RATING_VALUES;
    return RatingColors[Number(rating) - 1];
  }

  categoryTitle(catId: AppProductType['key']) {
    return this.categorySvc.categoryTitle(catId) || 'Unknown bill';
  }

  /**
   * Generates array for looping stars based upon length provided.
   * @param length
   */
  generateArray(length: number): number[] {
    return generateArray(length);
  }

  moreInfoToggle() {
    if (this.infoDetails && this.infoDetails.nativeElement) {
      const el = this.infoDetails.nativeElement as HTMLElement;
      const scrollHeight = el.scrollHeight;
      const clientHeight = el.clientHeight;

      if (clientHeight === scrollHeight) {
        el.style.height = '0px';
        el.style.opacity = '0';
        el.classList.add('collapsed');
      } else {
        el.style.height = scrollHeight + 'px';
        el.style.opacity = '1';

        if (el.classList.contains('collapsed')) {
          el.classList.remove('collapsed');
        }
      }

      this.showMoreInfo = !this.showMoreInfo;

      setTimeout(
        function () {
          if (this.showMoreInfo) {
            this.scrollToBottom();
          } else {
            this.scrollToTop();
          }
        }.bind(this),
        500,
      );
    }
  }

  scrollToTop() {
    this.logger.debug('BillPage::scrollToTop() => call');

    if (this.content) {
      this.logger.debug('BillPage::scrollToTop() => scroll content');
      this.content.scrollToTop(800);
    }
  }

  scrollToBottom() {
    this.logger.debug('BillPage::scrollToBottom() => call');

    if (this.content) {
      this.logger.debug('BillPage::scrollToBottom() => scroll content');
      this.content.scrollToBottom(800);
    }
  }

  saveTag() {
    const previousTagName = this.billDetails.billTag;
    this.isBillEditing = false;
    if (!this.tagName) return;
    this.savingTag = true;

    this.billService
      .updateTag({
        accountBillTypeId: this.billDetails.accountBillTypeId,
        tag: this.tagName,
      })
      .subscribe(
        () => {
          this.savingTag = false;
          this.refreshData();
        },

        (err) => {
          this.tagName = previousTagName;
          this.savingTag = false;
        },
      );
  }

  async presentToast(color: 'success' | 'danger' | 'warning', message: string) {
    const toast = await this.toastController.create({
      message,
      duration: APP_CONFIG.TOAST_SUCCESS_DURATION,
      color,
      buttons: [
        {
          icon: 'close-circle-outline',
          role: 'cancel',
        },
      ],
    });
    toast.present();
    color === 'success' ? this.refreshData() : '';
  }

  async presentToastAddCard(success: boolean, message: string) {
    const toast = await this.toastController.create({
      message: message,
      duration: 10000,
      color: success ? 'success' : 'warning',
      buttons: [
        {
          text: 'Go',
          icon: 'arrow-redo-outline',
          side: 'end',
          handler: () => {
            this.navCtrl.navigateForward([ROUTE_SETTINGS_PAYMENT_INFO]);
          },
        },
      ],
    });
    toast.present();
    success ? this.refreshData() : '';
  }

  async markAsPaidAlert(transaction?: YodleePossibleTransaction) {
    const alert = await this.alertController.create({
      mode: 'ios',
      cssClass: 'bill-alert',
      header: 'Are you sure?',
      message: 'We will stop sending reminders about this bill if marked as paid',
      buttons: [
        {
          text: 'Cancel',
          role: 'cancel',
          cssClass: 'secondary',
          handler: () => {
            this.logger.log('Confirm Cancel: blah');
          },
        },

        {
          text: 'Confirm',
          handler: () => {
            this.markBillAsPaid(transaction);
          },
        },
      ],
    });

    await alert.present();
  }

  async markBillAsPaid(transaction: YodleePossibleTransaction) {
    const loading = await this.loadingController.create({
      cssClass: 'my-custom-class',
      message: 'Please wait...',
      duration: 2000,
    });
    await loading.present();
    const date = transaction?.date;
    let showCheckDirectDebitForBill = false;
    this.api
      .paymentMarkPaid({
        billPaymentId: this.billPaymentId,
        date,
      })
      .pipe(
        tap((res) =>
          res.success && !this.billDetails.isDirectDebitSetup
            ? (showCheckDirectDebitForBill = true)
            : '',
        ),
        mergeMap((res) => {
          if (date)
            return this.yodleeService
              .updateTransaction(
                transaction?.transactionId,
                {
                  added: true,
                },

                transaction?.accountType,
              )
              .pipe(map((x) => res));
          return of(res);
        }),
        tap(() => this.yodleeService.getTransactions()),
      )
      .subscribe(
        (res) => {
          this.billPaid = true;
          this.logger.log(res, 'successfully paid!');
          this.presentToast('success', 'Bill marked paid successfully.');
          this.homeService.triggerFetchHomeData('BillPage::markBillAsPaid()', true);
        },

        (err) => {
          this.logger.error('paymentMarkPaid', err);
          this.presentToast('danger', 'Error in marking bill as paid.');
        },

        () => {
          // Refresh, dismiss loading alert, then present checkDirectDebitForBill
          this.billService.getBill(true).subscribe({
            next: () => {
              loading.dismiss();
              if (showCheckDirectDebitForBill == true) this.checkDirectDebitForBill();
            },
          });
        },
      );
  }

  async markAsUnpaidAlert(transaction?: YodleePossibleTransaction) {
    const alert = await this.alertController.create({
      mode: 'ios',
      cssClass: 'bill-alert',
      header: 'Are you sure?',
      message:
        'We will start sending reminders about this bill as it will be marked as unpaid',
      buttons: [
        {
          text: 'Cancel',
          role: 'cancel',
          cssClass: 'secondary',
          handler: () => {
            this.logger.log('Confirm Cancel: blah');
          },
        },

        {
          text: 'Confirm',
          handler: () => {
            this.markBillAsUnpaid(transaction);
          },
        },
      ],
    });

    await alert.present();
  }

  async markBillAsUnpaid(transaction: YodleePossibleTransaction) {
    const loading = await this.loadingController.create({
      cssClass: 'my-custom-class',
      message: 'Please wait...',
      duration: 2000,
    });
    await loading.present();
    const date = transaction?.date;
    let showCheckDirectDebitForBill = false;
    this.api
      .paymentMarkUnpaid({
        billPaymentId: this.billPaymentId,
        date,
      })
      .pipe(
        tap((res) =>
          res.success && !this.billDetails.isDirectDebitSetup
            ? (showCheckDirectDebitForBill = true)
            : '',
        ),
        mergeMap((res) => {
          if (date)
            return this.yodleeService
              .updateTransaction(
                transaction?.transactionId,
                {
                  added: true,
                },

                transaction?.accountType,
              )
              .pipe(map((x) => res));
          return of(res);
        }),
        tap(() => this.yodleeService.getTransactions()),
      )
      .subscribe(
        (res) => {
          if (res.success == true) {
            this.billPaid = false;
            this.logger.log(res, 'successfully paid!');
            this.presentToast('success', 'Bill marked as Unpaid successfully.');
            this.homeService.triggerFetchHomeData('BillPage::markBillAsUnpaid()', true);
          } else {
            this.logger.error('ERROR: paymentMarkUnpaid unsuccessful');
            this.presentToast('danger', 'Error in marking bill as Unpaid.');
          }
        },

        (err) => {
          this.logger.error('paymentMarkUnpaid', err);
          this.presentToast('danger', 'Error in marking bill as Unpaid.');
        },

        () => {
          this.billService.getBill(true).subscribe({
            next: () => {
              loading.dismiss();
              if (showCheckDirectDebitForBill == true) this.checkDirectDebitForBill();
            },
          });
        },
      );
  }

  showBill() {
    this.navCtrl.navigateForward(['file', this.billPaymentId], {
      queryParams: {
        fileType: 'pdf',
        billTypeId: this.billDetails.accountBillTypeId,
      },
    });
  }

  refreshData() {
    this.billService.fetchBill(true).subscribe();
  }

  getDueText(bill) {
    if (bill.amount <= 0) {
      return '';
    }

    if (isBillOverdue(bill)) {
      return 'Overdue';
    }

    return 'Due';
  }

  async confirmDeleteBill() {
    if (this.billDetails?.paymentPending === 1 || billIsAwaitingApproval(this.billDetails)) {
      this.alertController
        .create({
          cssClass: 'bill-alert',
          header: 'Cannot delete this bill',
          message:
            'Sorry, this bill cannot be deleted as it is undergoing the payment process.',
          mode: 'ios',
          buttons: ['OK'],
        })
        .then((alert) => {
          alert.present();
        });
    } else {
      this.alertController
        .create({
          cssClass: 'bill-alert',
          header: 'Are you sure?',
          message: "We won't pay this bill automatically if the bill is deleted",
          mode: 'ios',
          buttons: [
            {
              text: 'Cancel',
              role: 'cancel',
              handler: () => {
                this.logger.log('Confirm Cancel: blah');
              },
            },

            {
              text: 'Confirm',
              handler: () => {
                this.ngZone.run(() => {
                  if (this.isBillFailed) {
                    this.deleteFailedJob(this.jobId);
                  } else {
                    this.deleteBill(this.billPaymentId);
                  }
                });
              },
            },
          ],
        })
        .then((alert) => {
          alert.present();
        });
    }
  }

  async confirmDirectDebitUpdate() {
    const alert = await this.alertController.create({
      cssClass: 'bill-alert',
      header: 'Direct Debit not enabled?',
      message: 'Please click confirm if Direct Debit is not enabled for the bill.',
      mode: 'ios',
      buttons: [
        {
          text: 'Cancel',
          role: 'cancel',
          handler: () => {
            this.logger.log('Confirm Cancel: blah');
          },
        },

        {
          text: 'Confirm',
          handler: () => {
            this.ngZone.run(() => {
              this.directDebitUpdateForBillType({
                billPaymentId: this.billPaymentId,
                directDebitFlag: 0,
              });
            });
          },
        },
      ],
    });
    await alert.present();
  }

  async checkDirectDebitForBill() {
    const alert = await this.alertController.create({
      cssClass: 'bill-alert',
      header: 'Is Direct Debit enabled for this bill?',
      message: 'Please confirm if Direct Debit is enabled for the bill with supplier.',
      mode: 'ios',
      buttons: [
        {
          text: 'Cancel',
          role: 'cancel',
          handler: () => {
            this.logger.log('Confirm Cancel: blah');
          },
        },

        {
          text: 'No, not enabled',
        },

        {
          text: 'Yes, enabled',
          handler: () => {
            this.ngZone.run(() => {
              this.directDebitUpdateForBillType({
                billPaymentId: this.billPaymentId,
                directDebitFlag: 1,
              });
            });
          },
        },
      ],
    });
    await alert.present();
  }

  async confirmEnableDirectDebitUpdate() {
    const alert = await this.alertController.create({
      cssClass: 'bill-alert',
      header: 'Direct Debit already enabled?',
      message: 'Please click confirm if Direct Debit is enabled for the bill with supplier.',
      mode: 'ios',
      buttons: [
        {
          text: 'Cancel',
          role: 'cancel',
          handler: () => {
            this.logger.log('Confirm Cancel: blah');
          },
        },

        {
          text: 'Confirm',
          handler: () => {
            this.ngZone.run(() => {
              this.directDebitUpdateForBillType({
                billPaymentId: this.billPaymentId,
                directDebitFlag: 1,
              });
            });
          },
        },
      ],
    });
    await alert.present();
  }

  async directDebitUpdateForBillType({ billPaymentId, directDebitFlag }) {
    const loading = await this.loadingController.create({
      cssClass: 'my-custom-class',
      message: 'Please wait...',
      duration: 2000,
    });
    await loading.present();

    this.api
      .updateDirectDebit({
        billPaymentId,
        directDebitFlag,
      })
      .subscribe(
        (res) => {
          this.billPaid = true;

          this.logger.log(
            ' Direct Debit updated successfully!',
            {
              billPaymentId,
              directDebitFlag,
            },

            res,
          );
          this.presentToast('success', 'Direct Debit updated successfully!');
          this.homeService.triggerFetchHomeData('BillPage::updateDirectDebit()', true);
        },

        (err) => {
          this.logger.error('updateDirectDebit', err);
          this.presentToast('danger', 'Error in updating Direct Debit settings!');
        },

        () => {
          this.billService.getBill(true).subscribe({
            next: () => {
              loading.dismiss();
            },
          });
        },
      );
  }

  async deleteBill(billPaymentId) {
    this.logger.log(billPaymentId);
    this.logger.log(this._source);

    const loading = await this.loadingController.create({
      cssClass: 'my-custom-class',
      message: 'Please wait...',
      duration: 2000,
    });
    await loading.present();

    this.api.deleteBill(billPaymentId).subscribe(
      (res) => {
        this.billPaid = true;
        this.logger.log(' Deleted successfully!', billPaymentId, res);
        this.presentToast('success', 'Bill deleted successfully.');
        this.homeService.triggerFetchHomeData('BillPage::deleteBill()', true);
        this.navCtrl.navigateBack([this._source || ROUTE_HOMEPAGE]);
        this.logger.debug('navigated to route from: ', this._source || ROUTE_HOMEPAGE);
      },

      (err) => {
        this.logger.error('deleteBill', err);
        this.presentToast('danger', 'Error in deleting bill.');
      },

      () => {
        loading.dismiss();
      },
    );
  }

  async deleteFailedJob(jobId) {
    const loading = await this.loadingController.create({
      cssClass: 'my-custom-class',
      message: 'Please wait...',
      duration: 2000,
    });
    await loading.present();

    this.api.deleteFailedJob(jobId).subscribe(
      (res) => {
        if (res.success) {
          this.billPaid = true;
          this.logger.log(' Job deleted successfully!', jobId, res);
          this.presentToast('success', 'Bill deleted successfully.');
          this.homeService.triggerFetchHomeData('BillPage::deleteFailedJob()', true);
          this.navCtrl.navigateBack([this._source || ROUTE_HOMEPAGE]);
          this.logger.debug('navigated to route from: ', this._source || ROUTE_HOMEPAGE);
        } else if (res.isLocked) {
          this.logger.log(' Job in locked state, cannot be deleted', jobId, res);
          this.presentToast(
            'warning',
            'This bill is currently under review and cannot be deleted.',
          );
        }
      },

      (err) => {
        this.logger.error('deleteFailedJob', err);
        this.presentToast('danger', 'Error in deleting failed job.');
      },

      () => {
        loading.dismiss();
      },
    );
  }

  canSwitch(key: BillCategoryKey) {
    return canSwitch(key);
  }

  displayBetterDealBtn(b: ViewBillType) {
    // OB-1229: Display better deal button on all bills
    return canSwitch(b.categoryKey);

    // if (
    //   b.categoryKey === BillCategoryKey.COUNCIL_RATES ||
    //   b.categoryKey === BillCategoryKey.WATER ||
    //   b.rating === 5
    // ) {
    //   return false;
    // } else {
    //   return true;
    // }
  }

  /**
   * @deprecated Unused?
   * @param billPaymentId
   */
  // payNow(billPaymentId: number) {
  //   this.billService.payBillNow({ billPaymentIds: [billPaymentId] }).subscribe((res) => {
  //     if (
  //       res.success == false &&
  //       res.code === BillPaymentFailedErrorCodes.CARD_DETAILS_MISSING
  //     ) {
  //       this.presentToastAddCard(res.success, 'Please add payment details to pay now.');
  //     } else {
  //       this.presentToast(res.success ? 'success' : 'danger', res.message);
  //     }
  //   });
  // }

  presentPaymentCombinedModal(viewBillType: ViewBillType) {
    this.billService.presentPaymentCombinedModal(viewBillType);
  }
}
